import { useState, useEffect, useRef, useCallback } from "react";
import { rewardsStore, replaceRewardsFromApi, type Reward } from "../../utils/rewardsStore";
import { useAdminData } from "../../AdminDataContext";
import { apiGet, apiPost, apiPut, apiDelete } from "../../../../api/http";
import { uploadTenantImageFile } from "../../../../api/tenantImageUpload";
import type { PointRewardDto } from "../../../../api/types";
import { reportError } from "../../../../api/errors";

const PRODUCT_TYPES = ["普通商品", "服装类", "数字权益", "门票类", "食品饮料"];
const CATEGORIES = ["球衣周边", "观赛体验", "配件装备", "收藏品", "生活用品", "数字内容"];
const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "XXXXL", "XXXXXL", "XXXXXXL", "均码"];

interface AddForm {
  name: string;
  points: number;
  stock: number;
  productType: string;
  category: string;
  description: string;
  images: string[];
  sizes: string[];
  weight: string;
  brand: string;
}

const defaultAddForm: AddForm = {
  name: "",
  points: 100,
  stock: 10,
  productType: "普通商品",
  category: "球衣周边",
  description: "",
  images: [""],
  sizes: [],
  weight: "",
  brand: "",
};

export default function RewardsConfig() {
  const { tenantLegacyId } = useAdminData();
  const [rewards, setRewards] = useState<Reward[]>(() => rewardsStore.getAll());
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [editForm, setEditForm] = useState({ name: "", points: 0, stock: 0 });
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState<AddForm>({ ...defaultAddForm });
  const [deleteId, setDeleteId] = useState<number | string | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Local image upload
  const imgUploadRef = useRef<HTMLInputElement>(null);
  const uploadingImgIndexRef = useRef<number>(0);

  const syncFromApi = useCallback(async () => {
    setSyncing(true);
    try {
      const rows = await apiGet<PointRewardDto[]>(`/api/tenants/${tenantLegacyId}/point-rewards`);
      replaceRewardsFromApi(
        rows.map((r) => ({
          id: r.id,
          name: r.name,
          points: r.points,
          stock: r.stock,
          cover: r.cover,
        }))
      );
    } catch {
      replaceRewardsFromApi([]);
    } finally {
      setSyncing(false);
    }
  }, [tenantLegacyId]);

  useEffect(() => {
    void syncFromApi();
  }, [syncFromApi]);

  useEffect(() => {
    const unsub = rewardsStore.subscribe(() => setRewards(rewardsStore.getAll()));
    return unsub;
  }, []);

  const openEdit = (r: Reward) => {
    setEditForm({ name: r.name, points: r.points, stock: r.stock });
    setEditingId(r.id);
  };

  const saveEdit = async () => {
    if (editingId === null || typeof editingId !== "number") return;
    try {
      await apiPut(`/api/tenants/${tenantLegacyId}/point-rewards/${editingId}`, {
        name: editForm.name.trim(),
        points: editForm.points,
        stock: editForm.stock,
      });
      await syncFromApi();
      setEditingId(null);
    } catch (e) {
      reportError(e, "保存失败");
    }
  };

  const handleAdd = async () => {
    if (!addForm.name.trim()) return;
    const firstImage = addForm.images.find((img) => img.trim()) || "";
    try {
      await apiPost(`/api/tenants/${tenantLegacyId}/point-rewards`, {
        name: addForm.name.trim(),
        points: addForm.points,
        stock: addForm.stock,
        cover: firstImage || undefined,
        description: addForm.description.trim() || undefined,
        productType: addForm.productType.trim() || undefined,
        brand: addForm.brand.trim() || undefined,
        specification: addForm.weight.trim() || undefined,
      });
      await syncFromApi();
      setAdding(false);
      setAddForm({ ...defaultAddForm });
    } catch (e) {
      reportError(e, "创建失败");
    }
  };

  const patchStock = async (r: Reward, nextStock: number) => {
    if (typeof r.id !== "number") return;
    try {
      await apiPut(`/api/tenants/${tenantLegacyId}/point-rewards/${r.id}`, { stock: nextStock });
      await syncFromApi();
    } catch (e) {
      reportError(e, "更新库存失败");
    }
  };

  const confirmDelete = async () => {
    if (deleteId === null || typeof deleteId !== "number") return;
    try {
      await apiDelete(`/api/tenants/${tenantLegacyId}/point-rewards/${deleteId}`);
      await syncFromApi();
      setDeleteId(null);
    } catch (e) {
      reportError(e, "删除失败");
    }
  };

  const addImageSlot = () => {
    if (addForm.images.length < 6) {
      setAddForm((p) => ({ ...p, images: [...p.images, ""] }));
    }
  };

  const updateImage = (index: number, value: string) => {
    setAddForm((p) => {
      const images = [...p.images];
      images[index] = value;
      return { ...p, images };
    });
  };

  const removeImage = (index: number) => {
    if (addForm.images.length <= 1) return;
    setAddForm((p) => ({ ...p, images: p.images.filter((_, i) => i !== index) }));
  };

  const toggleSize = (size: string) => {
    setAddForm((p) => ({
      ...p,
      sizes: p.sizes.includes(size) ? p.sizes.filter((s) => s !== size) : [...p.sizes, size],
    }));
  };

  const totalStock = rewards.reduce((s, r) => s + r.stock, 0);
  const outOfStock = rewards.filter((r) => r.stock === 0).length;
  const lowStock = rewards.filter((r) => r.stock > 0 && r.stock <= 5).length;

  const handleLocalImgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const idx = uploadingImgIndexRef.current;
    try {
      const url = await uploadTenantImageFile(file, tenantLegacyId);
      updateImage(idx, url);
    } catch (err) {
      reportError(err, "图片上传失败");
    }
    e.target.value = "";
  };

  const triggerImgUpload = (index: number) => {
    uploadingImgIndexRef.current = index;
    imgUploadRef.current?.click();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-bold text-gray-900">积分兑换商城管理</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            管理兑换商品信息、积分价格与库存数量
            {syncing && <span className="ml-2 text-amber-600">同步中…</span>}
          </p>
        </div>
        <button
          onClick={() => { setAddForm({ ...defaultAddForm }); setAdding(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700 cursor-pointer transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i>新增商品
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: "商品总数", value: rewards.length, icon: "ri-store-2-line", color: "text-red-600 bg-red-50" },
          { label: "总库存量", value: totalStock, icon: "ri-archive-stack-line", color: "text-teal-600 bg-teal-50" },
          { label: "库存预警", value: `${outOfStock}售罄 / ${lowStock}低库存`, icon: "ri-alert-line", color: outOfStock > 0 ? "text-red-600 bg-red-50" : "text-amber-600 bg-amber-50" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center gap-3">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${s.color}`}>
              <i className={`${s.icon} text-base`}></i>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-400">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Rewards list */}
      <div className="space-y-3">
        {rewards.map((r) => {
          const isLow = r.stock > 0 && r.stock <= 5;
          const isOut = r.stock === 0;
          return (
            <div key={r.id} className={`bg-white rounded-2xl border-2 p-4 flex items-center gap-4 ${isOut ? "border-red-100" : isLow ? "border-amber-100" : "border-gray-100"}`}>
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                <img src={r.cover} alt={r.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900">{r.name}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs font-bold text-red-600">{r.points} 积分</span>
                  <div className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${isOut ? "bg-red-50 text-red-600" : isLow ? "bg-amber-50 text-amber-600" : "bg-green-50 text-green-600"}`}>
                    <i className={`${isOut ? "ri-close-circle-line" : isLow ? "ri-alert-line" : "ri-checkbox-circle-line"} text-xs`}></i>
                    {isOut ? "已售罄" : `库存 ${r.stock} 件${isLow ? "（库存偏低）" : ""}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => void patchStock(r, r.stock - 1)}
                  disabled={r.stock <= 0 || typeof r.id !== "number" || syncing}
                  className="w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-subtract-line"></i>
                </button>
                <span className="w-8 text-center text-sm font-bold text-gray-800">{r.stock}</span>
                <button
                  onClick={() => void patchStock(r, r.stock + 1)}
                  disabled={typeof r.id !== "number" || syncing}
                  className="w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm cursor-pointer transition-colors"
                >
                  <i className="ri-add-line"></i>
                </button>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => openEdit(r)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-amber-600 cursor-pointer rounded-lg hover:bg-amber-50 transition-colors" title="编辑">
                  <i className="ri-edit-line text-sm"></i>
                </button>
                <button onClick={() => setDeleteId(r.id)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 cursor-pointer rounded-lg hover:bg-red-50 transition-colors" title="删除">
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {rewards.length === 0 && (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
          <i className="ri-store-2-line text-4xl text-gray-200 block mb-2"></i>
          <p className="text-sm text-gray-400">暂无商品，点击「新增商品」开始添加</p>
        </div>
      )}

      {/* Edit Modal */}
      {editingId !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-96 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-900">编辑商品信息</h3>
              <button onClick={() => setEditingId(null)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500"></i></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">商品名称</label>
                <input type="text" value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">积分价格</label>
                  <input type="number" min={0} value={editForm.points} onChange={(e) => setEditForm((p) => ({ ...p, points: Math.max(0, Number(e.target.value)) }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">库存数量</label>
                  <input type="number" min={0} value={editForm.stock} onChange={(e) => setEditForm((p) => ({ ...p, stock: Math.max(0, Number(e.target.value)) }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setEditingId(null)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
              <button onClick={() => void saveEdit()} className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm cursor-pointer hover:bg-red-700 whitespace-nowrap">保存</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal - Enhanced */}
      {adding && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-900">新增兑换商品</h3>
              <button onClick={() => setAdding(false)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500"></i></button>
            </div>
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">商品名称 *</label>
                <input type="text" value={addForm.name} onChange={(e) => setAddForm((p) => ({ ...p, name: e.target.value }))} placeholder="如：协会定制球衣" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" autoFocus />
              </div>

              {/* Type & Category */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">商品类型</label>
                  <select value={addForm.productType} onChange={(e) => setAddForm((p) => ({ ...p, productType: e.target.value, sizes: [] }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer">
                    {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">商品分类</label>
                  <select value={addForm.category} onChange={(e) => setAddForm((p) => ({ ...p, category: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Points & Stock */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">积分价格</label>
                  <input type="number" min={0} value={addForm.points} onChange={(e) => setAddForm((p) => ({ ...p, points: Math.max(0, Number(e.target.value)) }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">初始库存</label>
                  <input type="number" min={0} value={addForm.stock} onChange={(e) => setAddForm((p) => ({ ...p, stock: Math.max(0, Number(e.target.value)) }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
                </div>
              </div>

              {/* Brand & Weight */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">品牌/来源</label>
                  <input type="text" value={addForm.brand} onChange={(e) => setAddForm((p) => ({ ...p, brand: e.target.value }))} placeholder="如：协会官方" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">规格</label>
                  <input type="text" value={addForm.weight} onChange={(e) => setAddForm((p) => ({ ...p, weight: e.target.value }))} placeholder="如：500ml" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
                </div>
              </div>

              {/* Size options for clothing */}
              {addForm.productType === "服装类" && (
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1.5">
                    可选尺码 <span className="text-gray-400 font-normal">（可多选）</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {SIZES.map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => toggleSize(size)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg border-2 cursor-pointer transition-all whitespace-nowrap ${
                          addForm.sizes.includes(size)
                            ? "border-red-500 bg-red-50 text-red-700"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                  {addForm.sizes.length > 0 && (
                    <p className="text-xs text-gray-400 mt-1.5">
                      已选: {addForm.sizes.join(" / ")}
                    </p>
                  )}
                </div>
              )}

              {/* Product Images */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium text-gray-600">
                    商品图片 <span className="text-gray-400 font-normal">（第1张为封面，最多6张）</span>
                  </label>
                  {addForm.images.length < 6 && (
                    <button type="button" onClick={addImageSlot} className="text-xs text-red-600 cursor-pointer hover:underline whitespace-nowrap">
                      + 添加图片
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {addForm.images.map((img, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <span className="text-xs text-gray-400 w-4 flex-shrink-0 text-center">{idx + 1}</span>
                      {/* Preview thumbnail */}
                      {img ? (
                        <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden border border-gray-100 bg-gray-50">
                          <img src={img} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                        </div>
                      ) : (
                        <div className="w-10 h-10 flex-shrink-0 rounded-lg border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center">
                          <i className="ri-image-line text-gray-300 text-sm"></i>
                        </div>
                      )}
                      {/* Upload button */}
                      <button
                        type="button"
                        onClick={() => triggerImgUpload(idx)}
                        className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors whitespace-nowrap flex-shrink-0"
                        title="本地上传"
                      >
                        <i className="ri-upload-2-line text-xs"></i>
                        上传
                      </button>
                      <input
                        type="url"
                        value={img.startsWith("data:") ? "" : img}
                        onChange={(e) => updateImage(idx, e.target.value)}
                        placeholder={idx === 0 ? "粘贴封面图链接..." : `图片链接 ${idx + 1}`}
                        className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-red-400 min-w-0"
                      />
                      {img.startsWith("data:") && (
                        <span className="text-xs text-green-600 flex-shrink-0 whitespace-nowrap">本地</span>
                      )}
                      {addForm.images.length > 1 && (
                        <button type="button" onClick={() => removeImage(idx)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer rounded-lg hover:bg-red-50 flex-shrink-0">
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1.5">商品描述</label>
                <textarea
                  value={addForm.description}
                  onChange={(e) => setAddForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="详细描述商品信息、材质、使用说明等..."
                  rows={3}
                  maxLength={500}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 resize-none"
                />
                <p className="text-right text-xs text-gray-400 mt-1">{addForm.description.length}/500</p>
              </div>
            </div>

            <div className="flex gap-3 mt-5 pt-4 border-t border-gray-100">
              <button onClick={() => setAdding(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
              <button onClick={() => void handleAdd()} disabled={!addForm.name.trim() || syncing} className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm cursor-pointer hover:bg-red-700 disabled:opacity-50 whitespace-nowrap">创建商品</button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden image upload input */}
      <input ref={imgUploadRef} type="file" accept="image/*" className="hidden" onChange={handleLocalImgUpload} />

      {/* Delete confirm */}
      {deleteId !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-80">
            <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 text-center mb-2">删除商品</h3>
            <p className="text-sm text-gray-500 text-center mb-5">删除后商品将从兑换商城下架，无法恢复。</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => void confirmDelete()} className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl cursor-pointer hover:bg-red-700 whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
